import React, { useEffect, useState } from "react";
import { Alert, Button, ConfigProvider, Input, Segmented, Spin, theme } from "antd";
import { DesktopOutlined, EyeOutlined, MobileOutlined, PictureOutlined, PlusOutlined, ShopOutlined } from "@ant-design/icons";
import useBusinessResource from "../hooks/useBusinessResource";
import type { BusinessScope } from "../utils/businessResourceFreshness";
import API_CONFIG from "../config/api";

interface PreviewValues {
  menu_name?: string;
  description?: string;
  logo_url?: string;
  default_service_mode?: string;
  allow_self_service_orders?: boolean;
  primary_color?: string;
  accent_color?: string;
  secondary_color?: string;
}
interface Product {
  id: number;
  name: string;
  description?: string;
  image_url?: string;
  available_for_purchase?: boolean;
}

const color = (value: string | undefined, fallback: string) => /^#[0-9a-f]{6}$/i.test(value || "") ? value! : fallback;
const foreground = (value: string) => {
  const channels = [1, 3, 5].map((offset) => parseInt(value.slice(offset, offset + 2), 16) / 255)
    .map((channel) => channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4);
  return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722 > 0.179 ? "#000000" : "#ffffff";
};

export default function RestaurantDigitalMenuPreview({ values, scope, menuSlug, published, publicUrl, enabled }: {
  values: PreviewValues; scope: BusinessScope; menuSlug?: string; published: boolean; publicUrl: string; enabled: boolean;
}) {
  const [device, setDevice] = useState("mobile");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [failedImages, setFailedImages] = useState<string[]>([]);
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);
  const catalogue = useBusinessResource<{ products: Product[] }>({ ...scope,
    resource: "restaurant-digital-menu-preview", parameters: [menuSlug, query], active: true,
    enabled: enabled && published && Boolean(menuSlug),
    load: async (signal) => {
      const params = new URLSearchParams({ page: "1", limit: "4", search: query });
      const response = await fetch(`${API_CONFIG.publicMenuUrl(encodeURIComponent(menuSlug!))}/products?${params}`, { signal });
      const data = await response.json();
      if (!response.ok || !Array.isArray(data.products)) throw new Error("Le catalogue public ne peut pas être actualisé.");
      return { products: data.products };
    },
  });
  const primary = color(values.primary_color, "#173f5f");
  const accent = color(values.accent_color, "#be4349");
  const background = color(values.secondary_color, "#f5f8fb");
  const logo = values.logo_url?.trim();
  const serviceModes: Record<string, string> = { dine_in: "Sur place", takeaway: "À emporter", delivery: "Livraison" };
  const serviceMode = serviceModes[values.default_service_mode || "dine_in"];
  const imageUrl = (url: string) => /^https?:\/\//i.test(url) ? url : API_CONFIG.url(url.startsWith("/") ? url : `/${url}`);
  const failed = (url: string) => setFailedImages((current) => current.includes(url) ? current : [...current, url]);

  return <section className="restaurant-digital-menu__preview-panel" aria-label="Aperçu client">
    <div className="restaurant-digital-menu__preview-heading">
      <h2><EyeOutlined /> Aperçu client</h2>
      <Segmented aria-label="Format de l’aperçu" value={device} onChange={(value) => setDevice(String(value))} options={[
        { value: "mobile", label: <><MobileOutlined /> <span>Mobile</span></> },
        { value: "desktop", label: <><DesktopOutlined /> <span>Ordinateur</span></> },
      ]} />
    </div>
    <p className="restaurant-digital-menu__help">Vos textes et couleurs s’affichent ici avant l’enregistrement.</p>
    <div className={`restaurant-digital-menu__preview-stage restaurant-digital-menu__preview-stage--${device}`}>
      <ConfigProvider theme={{ algorithm: theme.defaultAlgorithm, token: { colorPrimary: primary } }}>
      <div className="restaurant-digital-menu__client" data-testid="digital-menu-client-preview" data-device={device} style={{
        "--menu-primary": primary, "--menu-on-primary": foreground(primary),
        "--menu-accent": accent, "--menu-on-accent": foreground(accent), "--menu-background": background,
      } as React.CSSProperties}>
        <div className="restaurant-digital-menu__client-header">
          {logo && !failedImages.includes(logo) ? <img className="restaurant-digital-menu__client-logo" src={imageUrl(logo)}
            alt="Logo du menu" onError={() => failed(logo)} /> : <ShopOutlined className="restaurant-digital-menu__client-symbol" />}
          <h3>{values.menu_name || "Votre menu"}</h3>
          {values.description && <p>{values.description}</p>}
          <span className="restaurant-digital-menu__service-mode"><ShopOutlined /> {serviceMode}</span>
        </div>
        <div className="restaurant-digital-menu__client-body">
          <Input.Search aria-label="Rechercher dans l’aperçu" placeholder="Rechercher un plat…" value={search}
            onChange={(event) => setSearch(event.target.value)} allowClear disabled={!published} />
          <h4>Notre menu</h4>
          {!published ? <p className="restaurant-digital-menu__catalog-message">Le catalogue public apparaîtra ici après la publication du menu.</p>
            : catalogue.loading && !catalogue.data ? <div className="restaurant-digital-menu__catalog-message"><Spin /> Chargement des plats…</div>
            : catalogue.data?.products.length ? <div className="restaurant-digital-menu__client-products">
              {catalogue.data.products.map((product) => <article key={product.id} className="restaurant-digital-menu__client-product">
                {product.image_url && !failedImages.includes(product.image_url) ? <img src={imageUrl(product.image_url)} alt={product.name}
                  onError={() => failed(product.image_url!)} /> : <PictureOutlined className="restaurant-digital-menu__product-fallback" aria-label="Photo indisponible" />}
                <div><strong>{product.name}</strong>{product.description && <p>{product.description}</p>}
                  {product.available_for_purchase === false && <small>Indisponible</small>}</div>
                {values.allow_self_service_orders && <Button shape="circle" className="restaurant-digital-menu__client-order"
                  icon={<PlusOutlined />} href={publicUrl} target="_blank" rel="noopener noreferrer"
                  aria-label={`Voir ${product.name} sur le menu public`} disabled={product.available_for_purchase === false} />}
              </article>)}
            </div> : !catalogue.error && <p className="restaurant-digital-menu__catalog-message">{query ? "Aucun plat ne correspond à cette recherche." : "Aucun plat dans le catalogue public."}</p>}
          {!values.allow_self_service_orders && <p className="restaurant-digital-menu__catalog-message">Consultation du menu uniquement.</p>}
        </div>
      </div>
      </ConfigProvider>
    </div>
    {Boolean(catalogue.error) && <Alert type="warning" showIcon message="Aperçu du catalogue indisponible"
      description={catalogue.data ? "Les derniers plats disponibles sont conservés." : "Vos réglages restent accessibles."}
      action={<Button size="small" onClick={() => { void catalogue.refresh().catch(() => {}); }}>Réessayer</Button>} />}
    {published && <p className="restaurant-digital-menu__help">Extrait du catalogue public enregistré. Ouvrez le menu pour voir tous les plats.</p>}
  </section>;
}
